// @givmo/org-console — the ONE square derivation (shell contract v3, T4.1).
//
// ══ WHY THIS LIVES HERE AND NOT IN EACH HOST'S REGISTRY ═══════════════════════════════════════
//
// Two host apps used to carry an APP-level `applyModuleAccess` — duplicated byte for byte and
// pinned identical — whose two exemptions were registry data: the suite's own home square and the
// current square. Contract v3 replaces the suite home with the Admin Console tile, and the rule for
// THAT tile is a rule about authority ("rendered iff the settled effective set carries an Admin
// Console capability") — which is exactly the kind of rule this package exists to write once. A
// third copy of it, in a class console that has no registry, is the drift OM-6 already ruled out.
//
// So the derivation is written ONCE, over a host-supplied table of `ModuleRoute` rows that carry the
// two v3 flags as DATA — `classDefault` (exactly one per table) and `adminConsole` (at most one) —
// plus the caller's standing. Both class registries and any class table feed this one function.
//
// ══ WHAT IT DECIDES ═════════════════════════════════════════════════════════════════════════════
//
//   * WHICH squares render, in table order: every module the standing does not report `absent`,
//     and — LAST, after the modules (D2) — the Admin Console tile, iff a settled capability list
//     carries one of its pages' capabilities. NEVER an `isGlobalAdmin` check (the build law).
//   * WHAT STATE each is in: the shared tile rule for a module — the five verdicts plus OM-33's
//     `coming-soon` for an announced module this organization is not offered; always `link` for the
//     Admin Console tile, which the backend reports no standing for (it is not a module).
//   * WHERE each goes: through the ONE resolver (`moduleHref`) against the LIVE origin — an in-app
//     path when this build serves the module, the configured URL verbatim otherwise, and NOTHING
//     when the build was given no address at all (a square is either real or absent).
//   * The request DECORATION on a locked/off square (the caller's own `accessRequest`), the
//     pending-count BADGE on the Admin Console tile, which square is CURRENT, and which is the
//     caller's chosen DEFAULT landing.
//
// ══ THE OPENABLE SET, AND WHY THE ADMIN CONSOLE IS NOT IN IT ═══════════════════════════════════
//
// The landing rule, the app popup's first section and the preferences dropdown all read "what can
// this person OPEN" — a `link` verdict WITH somewhere to go. The Admin Console tile is always a link
// for whoever holds it, and counting it would make a person with nothing openable read as having an
// app, land them on the organization's management page, and offer "Admin Console" as a landing
// choice. R1 says the tile never counts; `openableSquares` is where that is enforced.

import { moduleHref, type ModuleDestination, type ModuleRoute } from './module-routes';
import {
  tileVerdictFor,
  type ModuleAccessRequest,
  type ModuleStandings,
  type ModuleTileVerdict,
} from './module-verdict';
import { holdsAdminConsoleCapability } from './standing-read';

/**
 * One row of a host's console table — a {@link ModuleRoute} plus the two v3 flags, as DATA.
 *
 * A host may carry display fields on the same row (label, description, icon); the derivation
 * preserves the whole row on each square so a host maps it to its tile without a second lookup.
 */
export interface ConsoleTableRow extends ModuleRoute {
  /**
   * The class's default landing module — exactly ONE per table (T1.4). Where a person lands when
   * nothing more specific applies. Its destination is whatever the ONE resolver answers for it,
   * exactly as for any tile; an unmounted or unconfigured class default is simply unopenable.
   */
  classDefault?: true;
  /**
   * The organization's own management tile — at most ONE per table (T4.1). Not a backend module:
   * the catalog reports no standing for it, it renders from the caller's capabilities, it is always
   * a link, and it never counts as an app.
   */
  adminConsole?: true;
}

/** How one square renders, over a host row. */
export type ConsoleSquare<T extends ConsoleTableRow = ConsoleTableRow> = {
  row: T;
  /**
   * The rendered state. `absent` never appears — an absent module is dropped from the array.
   * `coming-soon` (OM-33) is a module announced to the class and not offered to this organization.
   */
  verdict: Exclude<ModuleTileVerdict, 'absent'>;
  /**
   * Where the square goes, through the ONE resolver. `null` means this build was given no address
   * for the module — the square renders as a statement (locked/off/setting-up) or not at all.
   */
  destination: ModuleDestination | null;
  /** The caller is standing in this module. */
  current: boolean;
  /** The caller's own latest access request on this module, when the standing carried one. */
  accessRequest?: ModuleAccessRequest;
} & (
  | { kind: 'module'; badge?: never }
  | {
      kind: 'admin-console';
      /** The pending-request count for the tile's badge, when non-zero. */
      badge?: number;
    }
);

export interface ConsoleSquareInputs {
  /** The caller's per-module standing, or `null` when the read reported none (permissive). */
  standings: ModuleStandings | null;
  /**
   * The caller's SETTLED capability list, or `null` while nothing is reported. The Admin Console
   * tile renders only on a settled positive — it appears when the read settles and never vanishes.
   */
  capabilities: readonly string[] | null;
  /** The live origin the ONE resolver compares against — `window.location.origin` at call time. */
  origin: string;
  /** Which module the caller is standing in (`currentModuleFor`), if any. */
  currentSlug?: string | null;
  /** Requests waiting on the caller, for the Admin Console tile's badge. */
  pendingAccessRequests?: number;
  /**
   * OM-33 — the modules "coming soon" to this organization (`StandingRead.comingSoonModules`):
   * announced to its class, live, not offered to it. Absent ⇒ none.
   */
  comingSoon?: readonly string[];
}

/** A table-shape violation, named. The derivation throws on any of these. */
export interface ConsoleTableProblem {
  code: 'no-class-default' | 'many-class-defaults' | 'many-admin-consoles' | 'duplicate-slug';
  slugs: string[];
}

/**
 * The shape pins on a host's table: exactly one `classDefault`, at most one `adminConsole`, no
 * duplicate slug. Exported so a host's registry test can pin its own table; the derivation runs it
 * on every call and THROWS, because a table with two class defaults is a programming error that
 * must fail the first render rather than silently pick one.
 */
export function consoleTableProblems(rows: readonly ConsoleTableRow[]): ConsoleTableProblem[] {
  const problems: ConsoleTableProblem[] = [];
  const defaults = rows.filter((row) => row.classDefault === true).map((row) => row.slug);
  const consoles = rows.filter((row) => row.adminConsole === true).map((row) => row.slug);
  const seen = new Set<string>();
  const duplicates: string[] = [];
  for (const row of rows) {
    if (seen.has(row.slug)) duplicates.push(row.slug);
    seen.add(row.slug);
  }
  if (defaults.length === 0) problems.push({ code: 'no-class-default', slugs: [] });
  if (defaults.length > 1) problems.push({ code: 'many-class-defaults', slugs: defaults });
  if (consoles.length > 1) problems.push({ code: 'many-admin-consoles', slugs: consoles });
  if (duplicates.length > 0) problems.push({ code: 'duplicate-slug', slugs: duplicates });
  return problems;
}

function assertConsoleTable(rows: readonly ConsoleTableRow[]): void {
  const problems = consoleTableProblems(rows);
  if (problems.length === 0) return;
  const described = problems.map(
    (p) => `${p.code}${p.slugs.length ? ` (${p.slugs.join(', ')})` : ''}`,
  );
  throw new Error(`Console table is malformed: ${described.join('; ')}`);
}

/**
 * Where a square goes, through the ONE resolver — with the one addition the resolver itself does
 * not make: a module that has NO in-console path but DOES have a configured URL is a cross-origin
 * module, and its square goes to that URL verbatim.
 *
 * `moduleHref` answers `null` for "not MOUNTED in this build", which is the right answer for the
 * gate and the sign-in door but too narrow for a square: a module served by another origin is
 * mounted nowhere here and still a real destination. So: mounted ⇒ the resolver's answer;
 * unmounted with an address ⇒ external, verbatim; no address ⇒ `null`, and the square is either a
 * statement or absent.
 */
export function squareDestination(route: ModuleRoute, origin: string): ModuleDestination | null {
  const resolved = moduleHref(route, origin);
  if (resolved !== null) return resolved;
  const configured = (route.href ?? '').trim();
  return configured.length > 0 ? { kind: 'external', href: configured } : null;
}

/**
 * The derivation. Table order is display order; the Admin Console tile is moved LAST (D2).
 *
 * ⚠ THE CURRENT MODULE IS NEVER FILTERED — you are already inside it, and a console that locked its
 * own square would be arguing with the page rendering it. It renders `link` at its own path.
 *
 * ⚠ NOT REPORTED IS PERMISSIVE for modules (every built tile is a link — the tile rule's own
 * posture) and RESTRICTIVE for the Admin Console tile (no tile until the capabilities settle) —
 * two opposite directions on purpose, each stated where it applies.
 */
export function deriveConsoleSquares<T extends ConsoleTableRow>(
  rows: readonly T[],
  inputs: ConsoleSquareInputs,
): ConsoleSquare<T>[] {
  assertConsoleTable(rows);
  const { standings, capabilities, origin } = inputs;
  const currentSlug = inputs.currentSlug ?? null;
  const pending = inputs.pendingAccessRequests ?? 0;
  const comingSoon = inputs.comingSoon ?? [];

  const modules: ConsoleSquare<T>[] = [];
  let adminConsole: ConsoleSquare<T> | null = null;

  for (const row of rows) {
    if (row.adminConsole === true) {
      if (!holdsAdminConsoleCapability(capabilities)) continue;
      const destination = squareDestination(row, origin);
      // A tile with nowhere to go is not a tile. The management page is a path in this console, so
      // a host that gives it no `path`/`href` has not mounted it — and then there is no tile.
      if (destination === null) continue;
      adminConsole = {
        kind: 'admin-console',
        row,
        verdict: 'link',
        destination,
        current: currentSlug === row.slug,
        ...(pending > 0 ? { badge: pending } : {}),
      };
      continue;
    }

    const current = currentSlug === row.slug;
    // A coming-soon module is never "current": its own pages answer not-found for an organization
    // that is not offered it (the gate reads it as absent), so the exemption below has nothing to
    // argue with — and the announcement is the truth about it.
    const announced = comingSoon.includes(row.slug);
    const verdict: ModuleTileVerdict =
      current && !announced ? 'link' : tileVerdictFor(standings, row.slug, comingSoon);
    if (verdict === 'absent') continue;
    const destination = squareDestination(row, origin);
    // A module this build was given no address for is not built here: it renders nothing, in every
    // state — a locked statement about an app nobody here can reach would name a decision that
    // cannot be taken on this surface.
    if (destination === null) continue;
    const standing = standings?.[row.slug];
    modules.push({
      kind: 'module',
      row,
      verdict,
      destination,
      current: current && verdict === 'link',
      ...(standing?.accessRequest === undefined ? {} : { accessRequest: standing.accessRequest }),
    });
  }

  return adminConsole === null ? modules : [...modules, adminConsole];
}

/**
 * The OPENABLE set: module squares whose verdict is `link` and which have somewhere to go.
 *
 * ⚠ THE ADMIN CONSOLE TILE IS EXCLUDED (R1) — see the header. A person whose only link is the
 * management tile has NOTHING openable, and every rule downstream must read it that way.
 */
export function openableSquares<T extends ConsoleTableRow>(
  squares: readonly ConsoleSquare<T>[],
): ConsoleSquare<T>[] {
  return squares.filter(
    (square) =>
      square.kind === 'module' && square.verdict === 'link' && square.destination !== null,
  );
}

/**
 * The apps this console can OPEN here (OM-95): every module row the derivation above would give
 * somewhere to go, plus the modules the host names as having no page of their own.
 *
 * ⚠ THE SAME GATE AS A SQUARE'S, ON THE SAME TABLE. A row with no destination renders no square,
 * and now offers no app in a list either, so the invite, the access editor and the Apps page say
 * what the grid says. The backend's catalog still decides what the organization is offered: this
 * only narrows it by what the build serves, which the backend cannot see.
 *
 * `pageless` is for a module that is real without a page — Connected AI, whose act happens in an AI
 * client — and is listed wherever the backend's catalog carries it.
 */
export function servedModuleSlugs(
  rows: readonly ConsoleTableRow[],
  origin: string,
  pageless: readonly string[] = [],
): string[] {
  const served = rows
    .filter((row) => row.adminConsole !== true && squareDestination(row, origin) !== null)
    .map((row) => row.slug);
  return [...new Set([...served, ...pageless])];
}

/** The table's class default slug, or `null` for a table that names none. */
export function classDefaultSlug(rows: readonly ConsoleTableRow[]): string | null {
  return rows.find((row) => row.classDefault === true)?.slug ?? null;
}
